import { useMemo, useState } from "react";
import HttpMethods from "system/types/enums/HttpMethods";
import useHttpRequest from "./use-http-request";
import authentication from "system/libraries/authentication";
import { IRetryOptions } from "system/types/interfaces";
import {
  PagesApiResponse,
  UserPageLayoutResponse,
  UserPageLayoutCreatePayload,
  UserPageLayoutUpdateRequest,
  WidgetResponse,
  PageItem,
} from "system/types/interfaces/IAdvancedReporting";
import { IComplianceApiResponse, ICompliancePayload, ICompliancePercentagePayload, ICompliancePercentageResponse, IComplianceSubjectsResponse, IDeviceExportPayload, IProblemEndpointPagedResponse, IDeviceExportPagedResponse, IPatchTuesdayCompliancePayload, IRoiReportResponse, IPatchTuesdayComplianceMachine } from "system/types/interfaces/IWidgetReporting";

const useApiAdvancedReporting = () => {
  const [auth] = useState(authentication);

  const apiUrl = useMemo<string>(
    () =>
      (process.env.REACT_APP_WIDGET_REPORT_API_URL ?? "") + "/api/v1/",
    []
  );

  const httpRequest = useHttpRequest(apiUrl, auth);

  return useMemo(
    () => ({
      auth,

      widgetReport: {
        // GET /widgetreport/widgets
        getWidgets: async (
          retryOptions?: IRetryOptions
        ): Promise<WidgetResponse[] | null> => {
          return await httpRequest(
            HttpMethods.get,
            "widgetreport/widgets",
            undefined,
            false,
            retryOptions
          );
        },

        // GET /widgetreport/pages/{tenantId}
        getPages: async (
          tenantId: number,
          signal?: AbortSignal,
          retryOptions?: IRetryOptions
        ): Promise<PagesApiResponse | null> => {
          return await httpRequest(
            HttpMethods.get,
            `widgetreport/pages/${tenantId}`,
            undefined,
            false,
            { ...retryOptions, signal },
          );
        },


        // GET /widgetreport/userpagelayout/{pageId}
        getUserPageLayout: async (
          pageId: number,
          retryOptions?: IRetryOptions
        ): Promise<UserPageLayoutResponse[] | null> => {
          return await httpRequest(
            HttpMethods.get,
            `widgetreport/userpagelayout/${pageId}`,
            undefined,
            false,
            retryOptions
          );
        },

        // POST /widgetreport/userpagelayout
        createUserPageLayout: async (
          payload: UserPageLayoutCreatePayload,
          retryOptions?: IRetryOptions
        ): Promise<boolean | null> => {
          return await httpRequest(
            HttpMethods.post,
            "widgetreport/userpagelayout",
            payload,
            false,
            retryOptions
          );
        },

        // PUT /widgetreport/userpagelayout
        updateUserPageLayout: async (
          payload: UserPageLayoutUpdateRequest[],
          retryOptions?: IRetryOptions
        ): Promise<boolean | null> => {
          return await httpRequest(
            HttpMethods.put,
            "widgetreport/userpagelayout",
            payload,
            false,
            retryOptions
          );
        },

        // DELETE /widgetreport/userpagelayout/{id}
        deleteUserPageLayout: async (
          id: number,
          retryOptions?: IRetryOptions
        ): Promise<boolean | null> => {
          return await httpRequest(
            HttpMethods.delete,
            `widgetreport/userpagelayout/${id}`,
            undefined,
            false,
            retryOptions
          );
        },

        // ── Compliance ──────────────────────────────────────────────────────
        /**
         * POST /widgetreport/compliance/{tenantId}
         */
        getComplianceData: async (
          tenantId: number,
          payload: ICompliancePayload,
          retryOptions?: IRetryOptions
        ): Promise<IComplianceApiResponse | null> => {
          return await httpRequest(
            HttpMethods.post,
            `widgetreport/compliance/${tenantId}`,
            payload,
            false,
            retryOptions
          );
        },

        /**
         * POST /widgetreport/compliancepercentage/{tenantId}
         */
        getCompliancePercentage: async (
          tenantId: number,
          payload: ICompliancePercentagePayload,
          retryOptions?: IRetryOptions
        ): Promise<ICompliancePercentageResponse | null> => {
          return await httpRequest(
            HttpMethods.post,
            `widgetreport/compliancepercentage/${tenantId}`,
            payload,
            false,
            retryOptions
          );
        },

        /**
         * GET /widgetreport/compliance/subjects/{tenantId}
         */
        getComplianceSubjects: async (
          tenantId: number,
          retryOptions?: IRetryOptions
        ): Promise<IComplianceSubjectsResponse | null> => {
          return await httpRequest(
            HttpMethods.get,
            `widgetreport/compliance/subjects/${tenantId}`,
            undefined,
            false,
            retryOptions
          );
        },

        /**
         * GET /widgetreport/problemendpoints/{fromDate}/{toDate}/{tenantId}
         */
        getProblemEndpoints: async (
          tenantId: number,
          fromDate: string,
          toDate: string,
          page = 1,
          pageSize = 50,
          search = '',
          problem = true,
          sort?: string,
          order?: 'asc' | 'desc',
          retryOptions?: IRetryOptions
        ): Promise<IProblemEndpointPagedResponse | null> => {
          const params = new URLSearchParams({
            page: String(page),
            pagesize: String(pageSize),
            problem: String(problem),
            ...(search ? { search } : {}),
            ...(sort ? { sort } : {}),
            ...(order ? { order } : {}),
          });
          return await httpRequest(
            HttpMethods.get,
            `widgetreport/problemendpoints/${fromDate}/${toDate}/${tenantId}?${params.toString()}`,
            undefined,
            false,
            retryOptions
          );
        },
        /**
        * GET /widgetreport/problemendpoints/{fromDate}/{toDate}/{tenantId}/csv
        * Returns a raw CSV file as an ArrayBuffer.
        * No query params — route does not accept them.
        */
        getProblemEndpointsCsv: async (
          tenantId: number,
          fromDate: string,
          toDate: string,
          problem = true,
          retryOptions?: IRetryOptions
        ): Promise<{ data: Blob; filename: string | null } | Blob | null> => {
          const params = new URLSearchParams({
            problem: String(problem),
          });
          return await httpRequest(
            HttpMethods.get,
            `widgetreport/problemendpoints/${fromDate}/${toDate}/${tenantId}/csv?${params.toString()}`,
            undefined,
            true,
            retryOptions
          );
        },

        /**
        * POST /widgetreport/deviceexport/{tenantId}
        */
        getDeviceExport: async (
          tenantId: number,
          payload: IDeviceExportPayload,
          page = 1,
          pageSize = 10,
          search = '',
          sort?: string,
          order?: 'asc' | 'desc',
          retryOptions?: IRetryOptions
        ): Promise<IDeviceExportPagedResponse | null> => {
          const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
            ...(search ? { search } : {}),
            ...(sort ? { sort } : {}),
            ...(order ? { order } : {}),
          });
          return await httpRequest(
            HttpMethods.post,
            `widgetreport/deviceexport/${tenantId}?${params.toString()}`,
            payload,
            false,
            retryOptions
          );
        },

        /**
         * POST /widgetreport/deviceexport/{tenantId}/csv
         * Returns a raw Excel file as an ArrayBuffer.
         */
        getDeviceExportCsv: async (
          tenantId: number,
          payload: IDeviceExportPayload,
          search = '',
          retryOptions?: IRetryOptions
        ): Promise<{ data: Blob; filename: string | null } | Blob | null> => {
          const params = new URLSearchParams({
            page: '0',
            pageSize: '0',
            ...(search ? { search } : {}),
          });
          return await httpRequest(
            HttpMethods.post,
            `widgetreport/deviceexport/${tenantId}/csv?${params.toString()}`,
            payload,
            true,
            retryOptions
          );
        },

        /**
        * POST /widgetreport/patchtuesdaycompliance/{tenantId}
        */
        getPatchTuesdayCompliance: async (
          tenantId: number,
          payload: IPatchTuesdayCompliancePayload,
          retryOptions?: IRetryOptions
        ): Promise<IPatchTuesdayComplianceMachine | null> => {
          return await httpRequest(
            HttpMethods.post,
            `widgetreport/patchtuesdaycompliance/${tenantId}`,
            payload,
            false,
            retryOptions
          );
        },

        /**
        * POST /widgetreport/patchtuesdaycompliance/{tenantId}/toplast?page=1&pageSize=20
        */
        getPatchTuesdayComplianceTopLast: async (
          tenantId: number,
          payload: IPatchTuesdayCompliancePayload,
          mode: 'worst' | 'top',
          page = 1,
          pageSize = 10,
          retryOptions?: IRetryOptions
        ): Promise<IPatchTuesdayComplianceMachine | null> => {
          const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
          });
          return await httpRequest(
            HttpMethods.post,
            `widgetreport/patchtuesdaycompliance/${tenantId}/toplast?${params.toString()}`,
            payload,
            false,
            retryOptions
          );
        },

        getRoiReport: async (
          tenantId: number,
          fromDate: string,
          toDate: string,
          retryOptions?: IRetryOptions
        ): Promise<IRoiReportResponse | null> => {
          return await httpRequest(
            HttpMethods.get,
            `widgetreport/roi/${fromDate}/${toDate}/${tenantId}`,
            undefined,
            false,
            retryOptions
          );
        },

        createPage: async (
          payload: Omit<PageItem, 'Id' | 'CreatedAt' | 'UpdatedAt'>,
          retryOptions?: IRetryOptions
        ): Promise<PageItem | null> => {
          const response = await httpRequest(
            HttpMethods.post,
            'widgetreport/page',
            payload,
            false,
            retryOptions
          );
          return (response as any)?.received ?? response;
        },

        // PUT /widgetreport/page
        updatePage: async (
          payload: Partial<PageItem>,
          retryOptions?: IRetryOptions
        ): Promise<PageItem | null> => {
          const response = await httpRequest(
            HttpMethods.put,
            'widgetreport/page',
            payload,
            false,
            retryOptions
          );
          return (response as any)?.received ?? response;
        },

        // DELETE /widgetreport/page/{id}
        deletePage: async (
          pageId: number,
          retryOptions?: IRetryOptions
        ): Promise<boolean | null> => {
          return await httpRequest(
            HttpMethods.delete,
            `widgetreport/page/${pageId}`,
            undefined,
            false,
            retryOptions
          );
        },
      }
    }),
    [auth, httpRequest]
  );
};

export default useApiAdvancedReporting;